'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { api } from '@/lib/api';
import { Collection } from '@/types';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast';
import { useRouter } from 'next/navigation';

interface Props {
  promptId: string;
  initialSaved: boolean;
  /** Called whenever the "default Saved" state changes (add/remove from SavedPrompt). */
  onSavedChange?: (saved: boolean) => void;
  /** Visual variant — icon-only (circle, for card hover overlay) or pill (detail page primary CTA). */
  variant?: 'icon' | 'pill';
  /** Anchor alignment for the popover. */
  align?: 'left' | 'right';
}

/**
 * Single entry point for saving a prompt. Clicking the trigger opens a popover
 * that shows:
 *   - A checkbox for "Saved" (the default bookmark list)
 *   - One row per user collection with checkboxes
 *   - An inline input to create a new collection (with public/private toggle)
 *
 * Replaces the previous split of "Save toggle" + "Add to collection" buttons.
 */
export default function SavePopover({
  promptId, initialSaved, onSavedChange, variant = 'icon', align = 'right',
}: Props) {
  const router = useRouter();
  const toast = useToast();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(initialSaved);
  const [savePending, setSavePending] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIsPublic, setNewIsPublic] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; minW: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // React Portal needs to wait until the client is mounted (no SSR)
  useEffect(() => { setMounted(true); }, []);

  // Sync initial saved if parent sends a new value (e.g. after a card re-render)
  useEffect(() => { setSaved(initialSaved); }, [initialSaved, promptId]);

  // Click outside closes (works with portaled popover: check both trigger + popover)
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Compute popover position relative to the trigger. We pin to the
  // bottom-right/-left of the button and let it fit inside the viewport.
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    const compute = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const MIN_W = 260;
      const GAP = 8;
      // Prefer right-aligning (popover's right edge flush with trigger's right).
      const rightEdge = align === 'left' ? rect.left + MIN_W : rect.right;
      let left = rightEdge - MIN_W;
      // Keep inside the viewport with an 8px margin.
      const margin = 8;
      if (left < margin) left = margin;
      if (left + MIN_W > window.innerWidth - margin) left = window.innerWidth - MIN_W - margin;
      // Flip to above if too close to bottom edge
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < 320
        ? Math.max(margin, rect.top - 320 - GAP)
        : rect.bottom + GAP;
      setCoords({ top, left, minW: MIN_W });
    };
    compute();
    const onChange = () => setOpen(false); // close on scroll/resize so we don't chase a moving target
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open, align]);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const [cols, ids] = await Promise.all([
        api.collections.list(),
        api.collections.byPrompt(promptId),
      ]);
      setCollections(cols);
      setMemberOf(new Set(ids));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const openPopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) { router.push('/auth/login'); return; }
    setOpen(true);
    loadCollections();
  };

  const toggleSaved = async () => {
    if (savePending) return;
    setSavePending(true);
    const prev = saved;
    try {
      if (prev) {
        await api.prompts.unsave(promptId);
        setSaved(false); onSavedChange?.(false);
        toast.info('Removed from Saved');
      } else {
        await api.prompts.save(promptId);
        setSaved(true); onSavedChange?.(true);
        toast.success('Saved');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setSavePending(false);
    }
  };

  const toggleCollection = async (c: Collection) => {
    if (pendingId) return;
    setPendingId(c.id);
    const isIn = memberOf.has(c.id);
    try {
      if (isIn) {
        await api.collections.removeItem(c.id, promptId);
        setMemberOf((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
        setCollections((prev) => prev.map((x) => x.id === c.id ? { ...x, item_count: Math.max(0, x.item_count - 1) } : x));
        toast.info(`Removed from "${c.name}"`);
      } else {
        await api.collections.addItem(c.id, promptId);
        setMemberOf((prev) => { const n = new Set(prev); n.add(c.id); return n; });
        setCollections((prev) => prev.map((x) => x.id === c.id ? { ...x, item_count: x.item_count + 1 } : x));
        toast.success(`Added to "${c.name}"`);
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setPendingId(null);
    }
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setPendingId('__new__');
    try {
      const created = await api.collections.create({ name, is_public: newIsPublic });
      await api.collections.addItem(created.id, promptId);
      setCollections((prev) => [...prev, { ...created, item_count: 1 }]);
      setMemberOf((prev) => { const n = new Set(prev); n.add(created.id); return n; });
      setNewName('');
      setNewIsPublic(false);
      toast.success(`Created "${name}" and added`);
    } catch {
      toast.error('Couldn\u2019t create');
    } finally {
      setPendingId(null);
    }
  };

  const memberCount = memberOf.size + (saved ? 1 : 0);

  const triggerActive = saved || memberOf.size > 0;

  return (
    <>
      {variant === 'icon' ? (
        <button
          ref={triggerRef}
          onClick={openPopover}
          className={triggerActive ? 'active' : ''}
          aria-label="Save"
          title={saved ? 'Saved' : 'Save'}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 14, fontVariationSettings: triggerActive ? "'FILL' 1" : "'FILL' 0" }}
          >
            bookmark
          </span>
        </button>
      ) : (
        <button
          ref={triggerRef}
          onClick={openPopover}
          className={`flex items-center justify-center gap-2 py-3.5 px-6 rounded-full font-medium text-sm border transition-all ${
            triggerActive
              ? 'border-primary bg-primary-fixed text-primary'
              : 'border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary'
          }`}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 18, fontVariationSettings: triggerActive ? "'FILL' 1" : "'FILL' 0" }}
          >
            bookmark
          </span>
          {saved ? 'Saved' : 'Save'}
          {memberOf.size > 0 && (
            <span style={{
              fontSize: 11, padding: '1px 7px',
              background: 'var(--accent-soft)', color: 'var(--accent-ink)',
              borderRadius: 'var(--radius-full)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {memberCount}
            </span>
          )}
        </button>
      )}

      {mounted && open && createPortal(
        <div
          ref={popoverRef}
          className="coll-picker"
          style={{
            position: 'fixed',
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            minWidth: coords?.minW ?? 260,
            zIndex: 100,
            visibility: coords ? 'visible' : 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="coll-picker-title">Save to</div>

          {/* Default Saved row */}
          <button
            className={`coll-picker-row ${saved ? 'active' : ''}`}
            onClick={toggleSaved}
            disabled={savePending}
            aria-busy={savePending || undefined}
          >
            <span className="material-symbols-outlined">
              {saved ? 'check_box' : 'check_box_outline_blank'}
            </span>
            <span style={{ flex: 1 }}>Saved</span>
            <span className="coll-picker-row-count">default</span>
          </button>

          <div className="coll-picker-divider" />

          {loading ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
          ) : collections.length === 0 ? (
            <div style={{ padding: '6px 12px 10px', fontSize: 12, color: 'var(--ink-3)' }}>
              No collections yet. Create one below to organise.
            </div>
          ) : (
            collections.map((c) => {
              const isIn = memberOf.has(c.id);
              return (
                <button
                  key={c.id}
                  className={`coll-picker-row ${isIn ? 'active' : ''}`}
                  onClick={() => toggleCollection(c)}
                  disabled={pendingId === c.id}
                >
                  <span className="material-symbols-outlined">
                    {isIn ? 'check_box' : 'check_box_outline_blank'}
                  </span>
                  <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {c.name}
                    <span
                      className="material-symbols-outlined"
                      title={c.is_public ? 'Public' : 'Private'}
                      style={{ fontSize: 12, color: c.is_public ? 'oklch(0.6 0.14 160)' : 'var(--ink-4)' }}
                    >
                      {c.is_public ? 'public' : 'lock'}
                    </span>
                  </span>
                  <span className="coll-picker-row-count">{c.item_count}</span>
                </button>
              );
            })
          )}

          <div className="coll-picker-divider" />

          <div className="coll-picker-new" style={{ flexWrap: 'wrap', gap: 8 }}>
            <input
              placeholder="New collection…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createAndAdd(); }}
              style={{ flex: 1, minWidth: 120 }}
            />
            <button
              className="ps-btn ps-btn-primary ps-btn-sm"
              onClick={createAndAdd}
              disabled={!newName.trim() || pendingId === '__new__'}
              aria-busy={pendingId === '__new__' || undefined}
            >
              <span className="material-symbols-outlined ps-icon">add</span>
            </button>
            {newName.trim() && (
              <div
                style={{ flexBasis: '100%', display: 'flex', gap: 6, fontSize: 11, color: 'var(--ink-3)', alignItems: 'center' }}
              >
                Visibility:
                <button
                  type="button"
                  onClick={() => setNewIsPublic(false)}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    background: !newIsPublic ? 'var(--bg-soft)' : 'transparent',
                    border: '1px solid var(--line)',
                    color: !newIsPublic ? 'var(--ink)' : 'var(--ink-3)',
                    fontSize: 11,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 12 }}>lock</span>
                  Private
                </button>
                <button
                  type="button"
                  onClick={() => setNewIsPublic(true)}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    background: newIsPublic ? 'var(--bg-soft)' : 'transparent',
                    border: '1px solid var(--line)',
                    color: newIsPublic ? 'var(--ink)' : 'var(--ink-3)',
                    fontSize: 11,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 12 }}>public</span>
                  Public
                </button>
              </div>
            )}
          </div>

          <Link
            href="/my/saved"
            className="coll-picker-row no-underline"
            style={{ color: 'var(--ink-3)', justifyContent: 'center', marginTop: 2 }}
            onClick={() => setOpen(false)}
          >
            Manage collections →
          </Link>
        </div>,
        document.body,
      )}
    </>
  );
}
